import { CurrencyPipe, NgIf } from '@angular/common';
import { Component, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { cloneDeep } from 'lodash';
import { of, switchMap } from 'rxjs';
import { BANK_STATUS, FORMLY_FIELD_TYPES, FORMLY_FIELD_VALIDATORS } from '@app/constants';
import { DialogService, FeedbackService, ReportService } from '@app/core/services';
import { CellTransform, TableConfig } from '@app/interfaces';
import { BaseListComponent, TableComponent, TitleButton, TitleButtonsComponent, TitleComponent } from '@app/shared/components';
import { FiltersComponent, FormMode } from '@app/shared/forms/components';
import { BankService } from '@app/shared/services';
import { ReadOnly } from '@app/utils/claims';
import { AddBankModel } from '../models/bank-add.model';
import { BankListItem } from '../models/bank-list-item.model';


@Component({
	selector: 'sb-banking-list',
	standalone: true,
	imports: [TitleComponent, MatCardModule, FiltersComponent, TitleButtonsComponent, TableComponent, MatIconModule, NgIf],
	providers: [CurrencyPipe],
	templateUrl: './bank-list.component.html',
})
export class BankingListComponent extends BaseListComponent<BankListItem, BankService> {
	@ReadOnly() isReadOnly = true;
	@ViewChild('defaultCheckMark') defaultCheckMark: TemplateRef<any>;

	entityName: string = 'Bank';
	listingPagePath: string = '/lsb/system-settings/system-setup/banking';
	config: TableConfig<BankListItem> = this.getBankListConfig();
	titleButtons: TitleButton[] = this.getTitleButtons();
	protected defaultBankId: number = 0;

	addForm = new UntypedFormGroup({});
	addOptions: FormlyFormOptions = {
		formState: {
			create: true,
			mode: FormMode.Edit,
			readOnly: false,
		},
	};

	addModel = {
		ShortName: '',
		BankName: '',
		AccountNumber: '',
	} as AddBankModel;

	revertAddModel = {} as AddBankModel;

	constructor(
		protected readonly listService: BankService,
		protected readonly dialog: MatDialog,
		protected readonly feedbackService: FeedbackService,
		protected readonly route: ActivatedRoute,
		protected readonly router: Router,
		protected readonly reportService: ReportService,
		protected readonly currencyPipe: CurrencyPipe,
		private readonly dialogService: DialogService,
	) {
		super(dialog, feedbackService, route, router, reportService);
	}

	private getBankListConfig(): TableConfig<BankListItem> {
		return {
			...this.config,
			columns: [
				{
					header: 'Bank Code',
					key: 'ShortName',
				},
				{
					header: 'Bank Name',
					key: 'BankName',
				},
				{
					header: 'Status',
					key: 'BankStatusDisplay',
				},
				{
					header: 'Account #',
					key: 'AccountNumberDisplay',
				},
				{
					header: 'Current Balance',
					key: 'CurrentBalance',
					headerClass: 'linq-align-right',
					cellClass: 'linq-align-right',
					transform: ({ value }: CellTransform<BankListItem>): string =>
						value < 0
							? `-${this.currencyPipe.transform(Math.abs(value), 'USD', 'symbol', '1.2-2')}`
							: this.currencyPipe.transform(value, 'USD', 'symbol', '1.2-2') || '$0.00',
				},
				{
					header: 'Default',
					key: 'Id',
					disableSort: true,
					template: (): TemplateRef<any> => this.defaultCheckMark,
				},
			],
			actions: [
				{
					label: 'Toggle Default Bank',
					hidden: (row: BankListItem) => this.isReadOnly || row.BankStatusId !== BANK_STATUS.Active,
					onClick: (row: BankListItem): void => this.toggleDefaultBank(row),
				},
				{
					label: 'Delete Bank',
					hidden: (row: BankListItem) => !this.canDelete(row.Id),
					onClick: (row: BankListItem): void => this.deleteEntity(row.Id),
				},
			],
			defaultSortDirection: 'asc',
			showRowSelect: false,
			showRowActions: true,
			selectedId: null,
			defaultSortField: 'ShortName',
			onRowClick: (row: BankListItem) => this.editBank(row.Id),
		} as TableConfig<BankListItem>;
	}

	private getTitleButtons(): TitleButton[] {
		return [
			{
				label: 'Add Bank',
				icon: 'add',
				onClick: (): void => this.addBank(),
				hidden: () => this.isReadOnly,
			},
		];
	}

	protected afterOnInit(): void {
		this.listService.getDefaultBankId().subscribe({
			next: (defaultBankId: number) => (this.defaultBankId = defaultBankId),
		});
	}

	canDelete(id: any): boolean {
		const bank = this.data.find((b) => b.Id === id);
		return bank ? bank.CanDelete && !this.isReadOnly : false;
	}

	toggleDefaultBank(bank: BankListItem): void {
		const defaultBankId = bank.Id === this.defaultBankId ? 0 : bank.Id;
		this.listService.setDefaultBankId(defaultBankId).subscribe({
			next: () => {
				this.defaultBankId = defaultBankId;
				this.feedbackService.success('updated', 'Default Bank');
			},
			error: () => this.feedbackService.error('Failed to update default bank. Please try again.'),
		});
	}

	addBank(): void {
		this.feedbackService.clearAll();
		this.dialogService
			.open({
				title: 'Add Bank',
				fields: this.getAddFields(),
				model: this.addModel,
				options: this.addOptions,
				form: this.addForm,
				okText: 'Save',
			})
			.pipe(
				switchMap((result) => {
					if (!result) {
						this.addModel = cloneDeep(this.revertAddModel);
						this.addForm.markAsUntouched();

						return of(null);
					}

					const model = result as unknown as AddBankModel;
					return this.listService.addBank(model);
				}),
			)
			.subscribe((result) => {
				if (result) {
					this.editBank(result);
				}
			});
	}

	editBank(bankId: number): any {
		this.config.selectedId = bankId;
		this.router.navigate([`${this.listingPagePath}/${bankId}`]);
	}

	private getAddFields(): FormlyFieldConfig[] {
		return [
			{
				fieldGroup: [
					{
						key: 'ShortName',
						type: FORMLY_FIELD_TYPES.input,
						className: 'col-xs-4',
						props: {
							label: 'Bank Code',
							maxLength: 10,
							required: true,
						},
						validators: {
							validation: [FORMLY_FIELD_VALIDATORS.requiredNoWhitespace, FORMLY_FIELD_VALIDATORS.hasBegEndWhitespace],
							duplicate: {
								expression: (control: UntypedFormControl) => {
									return !this.data.find((bank) => bank.ShortName.toLocaleLowerCase() === control.value.toLocaleLowerCase());
								},
								message: 'Duplicate Bank Code',
							},
						},
					},
					{
						key: 'BankName',
						type: FORMLY_FIELD_TYPES.input,
						className: 'col-xs-8',
						props: {
							label: 'Bank Name',
							maxLength: 30,
							required: true,
						},
						validators: {
							validation: [FORMLY_FIELD_VALIDATORS.requiredNoWhitespace, FORMLY_FIELD_VALIDATORS.hasBegEndWhitespace],
						},
					},
					{
						key: 'AccountNumber',
						type: FORMLY_FIELD_TYPES.inputPositiveIntegers,
						className: 'col-xs-6',
						props: {
							label: 'Account #',
							maxLength: 17,
							required: true,
						},
						validators: {
							validation: [FORMLY_FIELD_VALIDATORS.requiredNoWhitespace],
							duplicate: {
								expression: (control: UntypedFormControl) => {
									return !this.data.find((bank) => bank.AccountNumber === control.value);
								},
								message: 'Duplicate Account Number',
							},
						},
					},
				],
			},
		];
	}
}
